import PropTypes from "prop-types";
import {useEffect, useRef, useState} from "react";
import ReactDOM from "react-dom";
import {useTranslation} from "react-i18next";
import {ConfEnv} from "../../config/config";
import privateRequest from "../../Infra/http/private.request";
import useCurrencyStore from "../../store/useCurrency.store";
import styles from "./ParallelGameModal.module.css";

const ParallelGameModal = ({game, onClose, onMinimize, isMinimized}) => {
  const {t} = useTranslation();
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [gameUrl, setGameUrl] = useState(null);
  const iframeRef = useRef(null);

  // Direct API call for parallel game initialization (independent of main game store)
  const initializeParallelGame = async (provider, gameId) => {
    const selectedCurrency = useCurrencyStore.getState().selectedCurrency;
    const currencyParam = selectedCurrency ? selectedCurrency.id : "VES";

    const url = `${ConfEnv.API_URL.GAMES_INIT}/${provider}/${gameId}?currency=${currencyParam}`;
    const response = await privateRequest.get(url);
    const {gameUrl: newGameUrl = null} = response?.data?.data || {};

    return newGameUrl;
  };

  // Initialize the game when component mounts or game changes
  useEffect(() => {
    const initializeGame = async () => {
      setIsLoading(true);
      setHasError(false);
      setGameUrl(null);

      try {
        const newGameUrl = await initializeParallelGame(game.provider, game.id);

        if (newGameUrl) {
          setGameUrl(newGameUrl);
          setIsLoading(false);
        } else {
          setHasError(true);
          setIsLoading(false);
        }
      } catch (_error) {
        setHasError(true);
        setIsLoading(false);
      }
    };

    if (game?.provider && game?.id) {
      initializeGame();
    }
  }, [game.provider, game.id]);

  const handleIframeLoad = () => {
    setIsLoading(false);
    setHasError(false);
  };

  const handleIframeError = () => {
    setIsLoading(false);
    setHasError(true);
  };

  const handleOverlayClick = e => {
    // Close modal if clicking on overlay background
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const modalContent = (
    <div
      className={styles.overlayContainer}
      onClick={handleOverlayClick}
    >
      <div className={`${styles.gameModal} ${isMinimized ? styles.minimized : ""}`}>
        {/* Modal Header */}
        <div className={styles.modalHeader}>
          <div className={styles.gameInfo}>
            <h3 className={styles.gameTitle}>{game.name || game.game_name || t("unknownGame", "Unknown Game")}</h3>
            <span className={styles.gameProvider}>
              {(game.provider_display_name || game.provider || t("unknownProvider", "Unknown")).toUpperCase()}
            </span>
          </div>

          <div className={styles.modalControls}>
            <button
              type="button"
              className={styles.minimizeButton}
              onClick={onMinimize}
              title={isMinimized ? t("maximize", "Maximize") : t("minimize", "Minimize")}
            >
              {isMinimized ? "□" : "−"}
            </button>
            <button
              type="button"
              className={styles.closeButton}
              onClick={onClose}
              title={t("close", "Close")}
            >
              ×
            </button>
          </div>
        </div>

        {/* Modal Content */}
        {!isMinimized && (
          <div className={styles.modalContent}>
            {isLoading && (
              <div className={styles.loadingContainer}>
                <div className={styles.loadingSpinner}></div>
                <p className={styles.loadingText}>{t("loadingGame", "Loading game...")}</p>
              </div>
            )}

            {hasError && (
              <div className={styles.errorContainer}>
                <div className={styles.errorIcon}>⚠️</div>
                <p className={styles.errorText}>{t("gameLoadError", "Failed to load game. Please try again.")}</p>
                <button
                  type="button"
                  className={styles.retryButton}
                  onClick={async () => {
                    setHasError(false);
                    setIsLoading(true);
                    try {
                      const newGameUrl = await initializeParallelGame(game.provider, game.id);
                      if (newGameUrl) {
                        setGameUrl(newGameUrl);
                        setIsLoading(false);
                      } else {
                        setHasError(true);
                        setIsLoading(false);
                      }
                    } catch (_error) {
                      setHasError(true);
                      setIsLoading(false);
                    }
                  }}
                >
                  {t("retry", "Retry")}
                </button>
              </div>
            )}

            {gameUrl && (
              <iframe
                ref={iframeRef}
                src={gameUrl}
                title={game.name || game.game_name}
                className={styles.gameIframe}
                onLoad={handleIframeLoad}
                onError={handleIframeError}
                allow="clipboard-read; clipboard-write; fullscreen; screen-wake-lock; accelerometer; gyroscope; magnetometer; microphone; camera; geolocation"
                style={{
                  display: isLoading || hasError ? "none" : "block",
                }}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );

  // Render modal using portal to ensure it appears on top
  return ReactDOM.createPortal(modalContent, document.body);
};

ParallelGameModal.propTypes = {
  game: PropTypes.shape({
    id: PropTypes.string.isRequired,
    provider: PropTypes.string.isRequired,
    name: PropTypes.string,
    game_name: PropTypes.string,
  }).isRequired,
  onClose: PropTypes.func.isRequired,
  onMinimize: PropTypes.func.isRequired,
  isMinimized: PropTypes.bool,
};

ParallelGameModal.defaultProps = {
  isMinimized: false,
};

export default ParallelGameModal;
